import { useTranslation } from 'react-i18next';

import type { ScheduleSummary } from '~/features/catalog-operations/types/catalog-ops-schedule.types';
import { cn } from '~/utils/cn';

/**
 * Status pill for a schedule row. Deliberately not `StatusChip` — that one is
 * typed to `BatchStatus`, and a schedule's state is a different vocabulary
 * (active/paused) crossed with the health of its last run.
 *
 * A failing schedule reads "Last run failed" rather than "Active": both are
 * true, and the failure is the one worth surfacing at a glance.
 */
export function ScheduleStatusChip({
  schedule,
}: {
  /** Only the fields the chip reads, so the cross-subsidiary Platform ▸ Schedules rows can
   *  render it too (passing `health: null` / `hasRunInFlight: false` there, where both have
   *  their own cells). */
  schedule: Pick<ScheduleSummary, 'id' | 'status' | 'health' | 'hasRunInFlight'>;
}) {
  const { t } = useTranslation('catalog-operations');

  const { label, className } = (() => {
    if (schedule.hasRunInFlight) {
      return {
        label: t('schedules.status.running'),
        className: 'bg-info-50 text-info',
      };
    }
    if (schedule.status === 'PAUSED') {
      return {
        label: t('schedules.status.paused'),
        className: 'bg-muted text-muted-foreground',
      };
    }
    if (schedule.health === 'FAILING') {
      return {
        label: t('schedules.status.failing'),
        className: 'bg-error-50 text-error',
      };
    }
    return { label: t('schedules.status.active'), className: 'bg-success-50 text-success' };
  })();

  return (
    <span
      data-testid={`schedule-status-chip-${schedule.id}`}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
        className,
      )}
    >
      <span className="size-1.5 shrink-0 rounded-full bg-current" aria-hidden="true" />
      {label}
    </span>
  );
}
